import { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Stack,
  Link,
  Typography,
  Toolbar,
  Divider,
  Button,
  IconButton,
  LinearProgress,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import Grid from '@mui/material/Grid';

import { useUser } from '@/providers/UserContext';

import LoadingMask from '@/components/shared/LoadingMask';
import ErrorSnackBar from '@/components/shared/ErrorSnackBar';

import { useApi } from '@/providers/ApiContext';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import { useNavigate, useParams } from 'react-router';
import { getInspection, deleteInspection } from '@/actions/inspection';
import { getManifest } from '@/actions/manifest';
import { getManifestChecks } from '@/actions/manifestCheck';
import INSPECTION_TYPES from '@/components/inspectionTypes';
import InspectionDeleteDialog from '../../InspectionDeleteDialog';
import toNiceTimestamp from '@/util/toNiceTimestamp';
import INSPECTION_STATUSES from '@/components/inspectionStatuses';
import { LoadingButton } from '@mui/lab';
import { runInspection, finalizeInspection } from '@/actions/inspection';
import InspectionDocuments from './InspectionDocuments';
import {
  getInspectionAsset,
  getInspectionAssetBinary,
} from '@/actions/inspectionAsset';
import DocumentViewDialog from '@/components/shared/DocumentViewDialog';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import type { Inspection as InspectionType } from '@/types/apiTypes';
import type { Manifest } from '@/types/apiTypes';
import type { EntityId } from '@/types/apiTypes';
import { getDocumentAsset } from '@/actions/documentAsset';
import { fetchAllInspectionChecksForManifest } from '@/util/fetchAllInspectionChecksForManifest';

const POLL_INTERVAL = 5000; // 5 sec

function Inspection(): React.JSX.Element {
  const user = useUser();
  const { inspection_id } = useParams<{ inspection_id: string }>();
  const navigate = useNavigate();

  const api = useApi();
  const [inspection, setInspection] = useState<
    | (InspectionType & {
        status: string;
        type: INSPECTION_TYPES;
        name: string;
        entity_id: EntityId;
        doc_content_id?: string;
        content_id?: string;
        critical_compliant?: number;
        critical_non_compliant?: number;
        non_critical_compliant?: number;
        non_critical_non_compliant?: number;
        manifests?: Array<{
          manifest_id?: EntityId;
          manifest_name?: string;
          reference_document_content_id?: string;
        }>;
        [key: string]: unknown;
      })
    | null
  >(null);
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const [docOpen, setDocOpen] = useState<boolean>(false);
  const [manifests, setManifests] = useState<
    | (Manifest & {
        checks?: Array<{
          section?: string;
          name: string;
          entity_id?: EntityId;
          reference?: string;
          [key: string]: unknown;
        }>;
        global?: boolean;
        reference_document_content?: string;
      })[]
    | null
  >(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [finalize_loading, set_finalize_loading] = useState<boolean>(false);
  const [finalize_confirm_open, set_finalize_confirm_open] =
    useState<boolean>(false);
  const [unrated_checks_count, set_unrated_checks_count] =
    useState<number>(0);
  const [unrated_check_loading, set_unrated_check_loading] =
    useState<boolean>(false);
  const [doc_content, setDocContent] = useState<string | null>(null);
  const [doc_title, setDocTitle] = useState<string | null>(null);
  const [content_manifest, setContentManifest] = useState<{
    report?: string;
    [key: string]: unknown;
  } | null>(null);
  const [sections, setSections] = useState<unknown>(null);
  const [pages, setPages] = useState<unknown>(null);
  const [sectionsOpen, setSectionsOpen] = useState<boolean>(false);
  const [api_error, setApiError] = useState<string | null>(null);

  const fetchInspection = useCallback(() => {
    if (user?.entity_id && inspection_id && api) {
      getInspection(api, inspection_id)
        .then((data) => {
          setInspection(
            data as InspectionType & {
              status: string;
              type: INSPECTION_TYPES;
              name: string;
              entity_id: EntityId;
              [key: string]: unknown;
            },
          );
        })
        .catch((error) => {
          console.log(error);
          navigate('/error', {
            state: {
              errorMessage: 'Failed to load inspection',
              errorDetails:
                (error as Error).message ||
                'Unable to retrieve the requested inspection. This could be due to network issues, server problems, or the inspection may no longer exist.',
              returnPath: '/quartermaster',
            },
          });
        });
    }
  }, [user?.entity_id, inspection_id, api, navigate]);

  useEffect(() => {
    if (user?.entity_id && !inspection) {
      fetchInspection();
    }
  }, [user?.entity_id, inspection_id, inspection, fetchInspection]);

  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;
    console.log('running effect');
    if (
      inspection &&
      [
        INSPECTION_STATUSES.PREPARING,
        INSPECTION_STATUSES.QUEUED,
        INSPECTION_STATUSES.RUNNING,
        INSPECTION_STATUSES.FINALIZING,
      ].includes(inspection.status as INSPECTION_STATUSES)
    ) {
      console.log('setting interval');
      interval = setInterval(fetchInspection, POLL_INTERVAL);
    }
    return () => {
      if (interval) {
        console.log('clearing interval');
        clearInterval(interval);
      }
    };
  }, [inspection, fetchInspection]);

  useEffect(() => {
    if (inspection?.doc_content_id && doc_content === null && api) {
      getInspectionAsset(
        api,
        inspection?.entity_id,
        `${inspection?.doc_content_id}/manifest.json`,
      )
        .then((asset) => {
          const document_manifest = asset as unknown as {
            sections?: string;
            pages?: string;
            formatted?: string;
            name?: string;
            [key: string]: unknown;
          };
          if (document_manifest?.sections && api) {
            getInspectionAsset(
              api,
              inspection?.entity_id,
              `${inspection?.doc_content_id}/${document_manifest.sections}`,
            )
              .then((data) => {
                setSections(data);
              })
              .catch((error) => {
                console.log(error);
                setSections(null);
              });
          } else {
            setSections(null);
          }
          if (document_manifest?.pages && api) {
            getInspectionAsset(
              api,
              inspection?.entity_id,
              `${inspection?.doc_content_id}/${document_manifest.pages}`,
            )
              .then((data) => {
                setPages(data);
              })
              .catch((error) => {
                console.log(error);
                setPages(null);
              });
          }
          if (document_manifest?.formatted && api) {
            getInspectionAsset(
              api,
              inspection?.entity_id,
              `${inspection?.doc_content_id}/${document_manifest.formatted}`,
            )
              .then((data) => {
                setDocContent(data as unknown as string);
                setDocTitle((document_manifest?.name as string) || null);
              })
              .catch((error) => {
                console.log(error);
                setDocContent('');
                setDocTitle('');
              });
          }
        })
        .catch((error) => {
          console.log(error);
          setDocContent('');
          setDocTitle('');
          setSections(null);
          setPages(null);
        });
    }
  }, [inspection, doc_content, api]);

  useEffect(() => {
    console.log(inspection);
    if (
      inspection?.status === INSPECTION_STATUSES.COMPLETE &&
      inspection?.content_id &&
      content_manifest === null &&
      api
    ) {
      getInspectionAsset(
        api,
        inspection?.entity_id,
        `${inspection?.content_id}/manifest.json`,
      )
        .then((asset) => {
          const manifest = asset as unknown as {
            report?: string;
            [key: string]: unknown;
          };
          console.log(manifest);
          setContentManifest(manifest);
        })
        .catch((error) => {
          console.log(error);
          setContentManifest({});
        });
    }
  }, [inspection, content_manifest, api]);

  useEffect(() => {
    if (!manifests && inspection && api) {
      if (
        [INSPECTION_STATUSES.PREPARING, INSPECTION_STATUSES.DRAFT].includes(
          inspection?.status as INSPECTION_STATUSES,
        )
      ) {
        const task = Promise.all(
          (inspection?.manifests || []).map(async (inspection_manifest) => {
            if (!inspection_manifest.manifest_id) return null;
            const [manifest, checks] = await Promise.all([
              getManifest(api, inspection_manifest.manifest_id),
              getManifestChecks(
                api,
                inspection_manifest.manifest_id,
                100, // need to move this whole part inside InspectionDocument at some point in order to make pagination work properly
              ),
            ]);
            return {
              ...manifest,
              checks: checks.results.sort((a, b) => {
                const aSection =
                  (a as { section?: string; name: string }).section || '';
                const bSection =
                  (b as { section?: string; name: string }).section || '';
                if (aSection === bSection) {
                  const aName = (a as { name: string }).name;
                  const bName = (b as { name: string }).name;
                  return aName > bName ? 1 : aName < bName ? -1 : 0;
                } else {
                  if (aSection === 'global') {
                    return -1;
                  } else if (bSection === 'global') {
                    return 1;
                  } else {
                    return aSection > bSection
                      ? 1
                      : aSection < bSection
                        ? -1
                        : 0;
                  }
                }
              }),
            };
          }),
        );
        task
          .then((data) => {
            setManifests(
              (
                data.filter(Boolean) as unknown as (Manifest & {
                  checks?: Array<{
                    section?: string;
                    name: string;
                    [key: string]: unknown;
                  }>;
                  global?: boolean;
                })[]
              ).sort((a, b) => {
                if (a.global) {
                  return -1;
                } else if (b.global) {
                  return 1;
                } else {
                  return a.name > b.name ? 1 : a.name < b.name ? -1 : 0;
                }
              }),
            );
          })
          .catch((error) => {
            console.log(error);
            navigate('/error', {
              state: {
                errorMessage: 'Failed to load inspection manifests',
                errorDetails:
                  (error as Error).message ||
                  'Unable to retrieve the inspection manifests. This could be due to network issues, server problems, or the inspection may be in an invalid state.',
                returnPath: '/quartermaster',
              },
            });
          });
      } else if (
        [INSPECTION_STATUSES.PENDING, INSPECTION_STATUSES.COMPLETE].includes(
          inspection?.status as INSPECTION_STATUSES,
        )
      ) {
        const task = Promise.all(
          (inspection?.manifests || []).map(async (inspection_manifest) => {
            if (!inspection_manifest.manifest_id) return null;
            const manifest = await getManifest(
              api,
              inspection_manifest.manifest_id,
            );

            if (
              manifest.reference_document?.entity_id &&
              manifest.reference_document?.content_id
            ) {
              const document_manifest: {
                type?: string;
                sections?: string;
                pages?: string;
                formatted?: string;
                name?: string;
              } = await getDocumentAsset(
                api,
                manifest.reference_document?.entity_id,
                manifest.reference_document?.content_id,
              );
              if (document_manifest?.formatted) {
                const reference_document_content = await getDocumentAsset(
                  api,
                  manifest.reference_document?.entity_id as string,
                  `${manifest.reference_document?.content_id}/${document_manifest.formatted}`,
                );
                return {
                  ...manifest,
                  reference_document_content,
                };
              }
            }
            return { ...manifest };
          }),
        );
        task
          .then((data) => {
            setManifests(
              (
                data.filter(Boolean) as unknown as (Manifest & {
                  global?: boolean;
                  reference_document_content?: string;
                })[]
              ).sort((a, b) => {
                if (a.global) {
                  return -1;
                } else if (b.global) {
                  return 1;
                } else {
                  return a.name > b.name ? 1 : a.name < b.name ? -1 : 0;
                }
              }),
            );
          })
          .catch((error) => {
            console.log(error);
            navigate('/error', {
              state: {
                errorMessage: 'Failed to load inspection manifests',
                errorDetails:
                  (error as Error).message ||
                  'Unable to retrieve the inspection manifests. This could be due to network issues, server problems, or the inspection may be in an invalid state.',
                returnPath: '/quartermaster',
              },
            });
          });
      } else {
        setManifests(
          (inspection?.manifests || [])
            .map((manifest) => {
              return {
                name: manifest.manifest_name || '',
                entity_id: manifest.manifest_id || '',
              } as Manifest & { global?: boolean };
            })
            .sort((a, b) => {
              if (a.global) {
                return -1;
              } else if (b.global) {
                return 1;
              } else {
                return a.name > b.name ? 1 : a.name < b.name ? -1 : 0;
              }
            }),
        );
      }
    }
  }, [inspection, manifests, api, navigate]);

  const handleFinalizeInspection = useCallback(async (): Promise<void> => {
    if (!inspection_id || !api) return;
    try {
      set_finalize_loading(true);
      const updated_inspection = await finalizeInspection(api, inspection_id);
      setInspection(
        updated_inspection as InspectionType & {
          status: string;
          type: INSPECTION_TYPES;
          name: string;
          entity_id: EntityId;
          [key: string]: unknown;
        },
      );
      set_finalize_loading(false);
    } catch (error) {
      console.error('Error finalizing inspection:', error);
      setApiError(
        (error as Error).message ||
          'Unable to finalize the inspection. This could be due to network issues, server problems, or the inspection may be in an invalid state.',
      );
      set_finalize_loading(false);
    }
  }, [inspection_id, api]);

  const countUnratedChecks = useCallback(async (): Promise<number> => {
    if (!inspection || !api || !manifests) {
      return 0;
    }

    const manifest_list = manifests ?? [];
    const docs_raw =
      inspection.type === INSPECTION_TYPES.EFFECTIVE
        ? (inspection['documents'] as unknown)
        : null;
    const docs: Array<Record<string, unknown> | null> =
      inspection.type === INSPECTION_TYPES.EFFECTIVE
        ? (Array.isArray(docs_raw)
            ? (docs_raw as Array<Record<string, unknown>>)
            : [])
        : [null];

    let count = 0;
    for (const doc of docs) {
      const document_entity_id =
        doc && typeof doc === 'object'
          ? (doc['document_entity_id'] as EntityId | undefined)
          : undefined;

      for (const manifest of manifest_list) {
        const rows = await fetchAllInspectionChecksForManifest(
          api,
          inspection.entity_id,
          document_entity_id,
          manifest.entity_id,
        );
        for (const row of rows) {
          if (row.rating === null || typeof row.rating === 'undefined') {
            count += 1;
          }
        }
      }
    }
    return count;
  }, [inspection, api, manifests]);

  const handleFinalizeClick = useCallback(async (): Promise<void> => {
    if (finalize_loading || unrated_check_loading) {
      return;
    }
    if (inspection?.status !== INSPECTION_STATUSES.PENDING) {
      await handleFinalizeInspection();
      return;
    }

    set_unrated_check_loading(true);
    try {
      const count = await countUnratedChecks();
      set_unrated_checks_count(count);
      if (count > 0) {
        set_finalize_confirm_open(true);
      } else {
        await handleFinalizeInspection();
      }
    } catch (error) {
      console.error('Error checking unrated inspections:', error);
      // If we can't verify, fall back to allowing finalize.
      await handleFinalizeInspection();
    } finally {
      set_unrated_check_loading(false);
    }
  }, [
    finalize_loading,
    unrated_check_loading,
    inspection?.status,
    countUnratedChecks,
    handleFinalizeInspection,
  ]);

  if (
    !inspection ||
    (inspection?.status === INSPECTION_STATUSES.DRAFT && !manifests)
  ) {
    return <LoadingMask />;
  }

  const base_path =
    inspection?.type === INSPECTION_TYPES.EFFECTIVE
      ? '/quartermaster/internalAudit'
      : '/quartermaster/evaluator';

  const handleDeleteInspection = async (): Promise<void> => {
    if (!inspection_id || !api) return;
    try {
      await deleteInspection(api, inspection_id);
      navigate(base_path);
    } catch (error) {
      console.error('Error deleting inspection:', error);
      setApiError(
        (error as Error).message ||
          'Unable to delete the inspection. This could be due to network issues, server problems, or insufficient permissions.',
      );
    }
  };

  const handleRunInspection = async (): Promise<void> => {
    if (!inspection_id || !api) return;
    try {
      setLoading(true);
      const updated_inspection = await runInspection(api, inspection_id);
      setInspection(
        updated_inspection as InspectionType & {
          status: string;
          type: INSPECTION_TYPES;
          name: string;
          entity_id: EntityId;
          [key: string]: unknown;
        },
      );
      setLoading(false);
    } catch (error) {
      console.error('Error running inspection:', error);
      setApiError(
        (error as Error).message ||
          'Unable to run the inspection. This could be due to network issues, server problems, or the inspection may be in an invalid state.',
      );
      setLoading(false);
    }
  };

  const handleDownloadReport = async (): Promise<void> => {
    if (!inspection || !content_manifest?.report || !api) return;
    try {
      // Make API call to fetch the file
      const response = await getInspectionAssetBinary(
        api,
        inspection?.entity_id,
        `${inspection?.content_id}/${content_manifest.report}`,
      );
      console.log(response);
      // Extract filename from Content-Disposition header, if provided
      const contentDisposition = response.headers['content-disposition'];
      let fileName = `${inspection.name} Inspection Report.docx`;
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = fileNameMatch[1];
        }
      }

      // Create a Blob from the response data
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/octet-stream',
      });

      // Create a temporary URL for the Blob
      const url = window.URL.createObjectURL(blob);

      // Create a temporary anchor element to trigger the download
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName; // Set the file name for download
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      setApiError(
        (error as Error).message ||
          'Unable to download the inspection report. This could be due to network issues, server problems, or the report may not be available.',
      );
    }
  };

  // JSON dialog for sections
  const renderSectionsDialog = (
    open: boolean,
    handleClose: () => void,
    docContent: string | null,
    sections: unknown,
    pages: unknown,
    title: string,
  ): React.JSX.Element => {
    return (
      <Dialog onClose={handleClose} open={open} maxWidth='xl' fullWidth>
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <b>{title}</b>
            <IconButton aria-label='close' onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box padding='10px' sx={{ maxWidth: 1800 }}>
            <Grid container spacing={2}>
              <Grid size={12}>
                <Typography variant='subtitle1' sx={{ mb: 1 }}>
                  Sections
                </Typography>
                <Paper
                  variant='outlined'
                  sx={{
                    p: 1,
                    backgroundColor: '#f5f5f5',
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    maxHeight: '600px',
                    overflow: 'auto',
                  }}
                >
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                    {JSON.stringify(sections, null, 2)}
                  </pre>
                </Paper>
              </Grid>
              {Boolean(pages) && (
                <Grid size={12}>
                  <Typography variant='subtitle1' sx={{ mb: 1 }}>
                    Pages
                  </Typography>
                  <Paper
                    variant='outlined'
                    sx={{
                      p: 1,
                      backgroundColor: '#f5f5f5',
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      maxHeight: '600px',
                      overflow: 'auto',
                    }}
                  >
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                      {JSON.stringify(pages, null, 2)}
                    </pre>
                  </Paper>
                </Grid>
              )}
              <Grid size={12}>
                <Typography variant='subtitle1' sx={{ mb: 1 }}>
                  Document Content
                </Typography>
                <Paper
                  variant='outlined'
                  sx={{
                    p: 1,
                    backgroundColor: '#f5f5f5',
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    maxHeight: '600px',
                    overflow: 'auto',
                  }}
                >
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                    {typeof docContent === 'object'
                      ? JSON.stringify(docContent, null, 2)
                      : docContent || ''}
                  </pre>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <>
      <Toolbar
        sx={{
          minHeight: '55px !important',
        }}
      >
        <IconButton sx={{ mr: 2 }} onClick={() => navigate(base_path)}>
          <ArrowBackOutlinedIcon />
        </IconButton>
        <Typography variant='h6' sx={{ mr: 4 }}>
          {inspection.name}
        </Typography>
        <Stack direction={'row'} spacing={2}>
          <Button
            onClick={() =>
              navigate({
                pathname: `${base_path}/edit`,
                search: `?inspection_id=${inspection_id}`,
              })
            }
            startIcon={<EditOutlinedIcon />}
          >
            Edit
          </Button>
          {[INSPECTION_STATUSES.COMPLETE].includes(
            inspection?.status as INSPECTION_STATUSES,
          ) ? (
            <Button
              onClick={handleDownloadReport}
              startIcon={<DownloadOutlinedIcon />}
              disabled={!content_manifest?.report}
            >
              Download
            </Button>
          ) : undefined}
          {[INSPECTION_STATUSES.DRAFT, INSPECTION_STATUSES.PREPARING].includes(
            inspection?.status as INSPECTION_STATUSES,
          ) ? (
            <Button
              onClick={() => setDeleteOpen(true)}
              startIcon={<DeleteOutlinedIcon />}
            >
              Delete
            </Button>
          ) : undefined}
        </Stack>
      </Toolbar>
      <Divider />

      <Box padding='15px'>
        <Typography variant='subtitle1'>Basic Information</Typography>
        <Divider />
        <br />
        <Grid
          container
          component='dl' // mount a Definition List
          spacing={0}
          sx={{ display: 'table' }}
        >
          <Grid>
            <Typography
              component='dt'
              variant='body1'
              sx={{ display: 'table-cell', width: '250px' }}
            >
              <b>ID:</b>
            </Typography>
            <Typography
              component='dd'
              variant='body2'
              sx={{ display: 'table-cell' }}
            >
              {inspection?.entity_id}
            </Typography>
          </Grid>
          <Grid>
            <Typography
              component='dt'
              variant='body1'
              sx={{ display: 'table-cell', width: '250px' }}
            >
              <b>Status:</b>
            </Typography>
            <Typography
              component='dd'
              variant='body2'
              sx={{ display: 'table-cell' }}
            >
              {inspection?.status}
            </Typography>
          </Grid>
          <Grid>
            <Typography
              component='dt'
              variant='body1'
              sx={{ display: 'table-cell', width: '250px' }}
            >
              <b>Created:</b>
            </Typography>
            <Typography
              component='dd'
              variant='body2'
              sx={{ display: 'table-cell' }}
            >
              {toNiceTimestamp(inspection?.created_at, true)}
            </Typography>
          </Grid>
          <Grid>
            <Typography
              component='dt'
              variant='body1'
              sx={{ display: 'table-cell', width: '250px' }}
            >
              <b>Last Updated:</b>
            </Typography>
            <Typography
              component='dd'
              variant='body2'
              sx={{ display: 'table-cell' }}
            >
              {toNiceTimestamp(inspection?.updated_at, true)}
            </Typography>
          </Grid>

          {inspection?.status === INSPECTION_STATUSES.COMPLETE ? (
            <>
              <Grid>
                <Typography
                  component='dt'
                  variant='body1'
                  sx={{ display: 'table-cell', width: '250px' }}
                >
                  <b>Critical Checks:</b>
                </Typography>
                <Typography
                  component='dd'
                  variant='body2'
                  sx={{ display: 'table-cell' }}
                >
                  {inspection?.critical_compliant || 0} /{' '}
                  {(inspection?.critical_compliant || 0) +
                    (inspection?.critical_non_compliant || 0)}
                </Typography>
              </Grid>
              <Grid>
                <Typography
                  component='dt'
                  variant='body1'
                  sx={{ display: 'table-cell', width: '250px' }}
                >
                  <b>Non-Critical Checks:</b>
                </Typography>
                <Typography
                  component='dd'
                  variant='body2'
                  sx={{ display: 'table-cell' }}
                >
                  {inspection?.non_critical_compliant || 0} /{' '}
                  {(inspection?.non_critical_compliant || 0) +
                    (inspection?.non_critical_non_compliant || 0)}
                </Typography>
              </Grid>
            </>
          ) : undefined}

          {Boolean(doc_content) ? (
            <>
              <Grid>
                <br />
                <Divider />
                <br />
              </Grid>
              <Grid sx={{ display: 'flex', alignItems: 'center' }}>
                <Link
                  component='button'
                  onClick={() => {
                    setDocOpen(true);
                  }}
                  sx={{ cursor: 'pointer' }}
                >
                  <Typography variant='h6'>View Document</Typography>
                </Link>
                {Boolean(sections) && import.meta.env.VITE_STAGE === 'dev' && (
                  <Button
                    onClick={() => setSectionsOpen(true)}
                    startIcon={<EditOutlinedIcon />}
                    sx={{ ml: 2 }}
                  >
                    View Contents
                  </Button>
                )}
                <DocumentViewDialog
                  title={doc_title || ''}
                  contents={doc_content || ''}
                  open={docOpen}
                  handleClose={() => setDocOpen(false)}
                />
                {renderSectionsDialog(
                  sectionsOpen,
                  () => setSectionsOpen(false),
                  doc_content,
                  sections,
                  pages,
                  'Document Content & Sections',
                )}
              </Grid>
            </>
          ) : undefined}
        </Grid>
      </Box>
      {inspection?.status === INSPECTION_STATUSES.PREPARING ? (
        <Box padding='15px'>
          <Typography variant='subtitle1'>Processing Draft Document</Typography>
          <LinearProgress />
        </Box>
      ) : (
        <>
          {inspection?.status === INSPECTION_STATUSES.DRAFT ? (
            <>
              <Divider />
              <Box padding='15px'>
                <LoadingButton
                  loading={loading}
                  variant='contained'
                  color='primary'
                  onClick={handleRunInspection}
                >
                  Run
                </LoadingButton>
              </Box>
            </>
          ) : undefined}
          {inspection?.status === INSPECTION_STATUSES.PENDING ? (
            <>
              <Divider />
              <Box padding='15px'>
                <LoadingButton
                  loading={finalize_loading || unrated_check_loading}
                  variant='contained'
                  color='primary'
                  onClick={() => void handleFinalizeClick()}
                >
                  Finalize
                </LoadingButton>
              </Box>
            </>
          ) : undefined}
          <Divider />
          <InspectionDocuments
            inspection={inspection}
            manifests={manifests || []}
          />
        </>
      )}
      {[INSPECTION_STATUSES.DRAFT, INSPECTION_STATUSES.PREPARING].includes(
        inspection.status as INSPECTION_STATUSES,
      ) ? (
        <InspectionDeleteDialog
          key={`delete_${inspection?.updated_at}`}
          inspection={inspection}
          open={deleteOpen}
          deleteInspection={handleDeleteInspection}
          handleClose={() => setDeleteOpen(false)}
        />
      ) : undefined}

      <Dialog
        open={finalize_confirm_open}
        onClose={() => set_finalize_confirm_open(false)}
        aria-labelledby='finalize-confirm-title'
      >
        <DialogTitle id='finalize-confirm-title'>
          Not all evaluation results have been reviewed
        </DialogTitle>
        <DialogContent>
          <Typography variant='body2'>
            {unrated_checks_count > 0
              ? `There ${unrated_checks_count === 1 ? 'is' : 'are'} ${unrated_checks_count} check${unrated_checks_count === 1 ? '' : 's'} that ${unrated_checks_count === 1 ? 'has' : 'have'} not been rated. Are you sure you want to continue?`
              : 'Not all evaluation results have been reviewed. Are you sure you want to continue?'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant='outlined'
            onClick={() => set_finalize_confirm_open(false)}
          >
            Cancel
          </Button>
          <LoadingButton
            variant='contained'
            color='primary'
            loading={finalize_loading}
            onClick={() => {
              set_finalize_confirm_open(false);
              void handleFinalizeInspection();
            }}
          >
            Continue
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <ErrorSnackBar message={api_error} onClear={() => setApiError(null)} />
    </>
  );
}

export default Inspection;
